import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  collection, getDocs, addDoc, setDoc, deleteDoc,
  doc, query, orderBy, serverTimestamp,
} from "firebase/firestore";
import {
  ref, uploadBytesResumable, getDownloadURL, deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase/firebase-config";

import imgContactMe   from "../assets/images/ContactMe.jpeg";
import imgJourney     from "../assets/images/Journey.jpeg";
import imgKnowMe      from "../assets/images/KnowMoreAboutMe.jpeg";
import imgLetsConnect from "../assets/images/LetsConnect.jpeg";
import imgSkills      from "../assets/images/Skills.jpeg";
import imgHeroJpg     from "../assets/images/hero.jpg";

/* Static site slots that never change */
const STATIC_SLOTS = [
  { value: "hero-bg",        label: "Hero Section Background" },
  { value: "contact-banner", label: "Contact Section Banner"  },
  { value: "skills-hero",    label: "Skills Page Hero"        },
  { value: "other",          label: "Other / Custom…"         },
];

/* Build slot list: dynamic journey phases + static slots */
const buildSlots = (phases) => {
  const journeySlots = phases.length > 0
    ? phases.map(p => ({
        value: `journey-${p.order}`,
        label: `Journey Ch.${p.order} — ${p.tag || p.title || `Chapter ${p.order}`}`,
      }))
    : [1, 2, 3, 4].map(n => ({
        value: `journey-${n}`,
        label: `Journey — Chapter ${n}`,
      }));
  return [...journeySlots, ...STATIC_SLOTS];
};

/* Resolve a slot value to a human label */
const resolveLabel = (slotValue, allSlots, customLabel) => {
  if (slotValue === "other") return customLabel || "Other";
  return allSlots.find(s => s.value === slotValue)?.label || slotValue || "Unlabelled";
};

const fmtSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/* ─────────────────────────────────────────────────
   UPLOAD FORM
───────────────────────────────────────────────── */
const UploadForm = ({ media, slots, onUploaded, onCancel }) => {
  const [slot, setSlot]           = useState(slots[0]?.value || "");
  const [customLabel, setCustom]  = useState("");
  const [uploading, setUploading] = useState(false);
  const [pct, setPct]             = useState(0);
  const [err, setErr]             = useState("");
  const fileRef = useRef();

  const existingForSlot = (s) =>
    s !== "other" ? media.find(m => m.slot === s) : null;

  const doUpload = async (file) => {
    setErr("");
    const existing = existingForSlot(slot);

    if (existing) {
      const name = resolveLabel(existing.slot, slots, existing.customLabel);
      const ok = window.confirm(
        `"${name}" already has a photo.\n\nReplace it? The old image will be deleted from storage.`
      );
      if (!ok) return;
    }

    setUploading(true);
    setPct(0);
    try {
      const storagePath = `library/${slot}/${Date.now()}_${file.name}`;
      const { url, path } = await new Promise((resolve, reject) => {
        const storageRef = ref(storage, storagePath);
        const task = uploadBytesResumable(storageRef, file);
        task.on(
          "state_changed",
          s => setPct(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
          reject,
          async () => resolve({ url: await getDownloadURL(task.snapshot.ref), path: task.snapshot.ref.fullPath })
        );
      });

      const payload = {
        url, path, slot,
        customLabel: slot === "other" ? customLabel.trim() : "",
        filename: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: serverTimestamp(),
      };

      if (existing) {
        if (existing.path) { try { await deleteObject(ref(storage, existing.path)); } catch (_) {} }
        await setDoc(doc(db, "media_library", existing.id), payload);
      } else {
        await addDoc(collection(db, "media_library"), payload);
      }
      onUploaded();
    } catch {
      setErr("Upload failed — check Firebase Storage rules.");
    } finally {
      setUploading(false);
      setPct(0);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await doUpload(file);
    e.target.value = "";
  };

  const existing = existingForSlot(slot);

  return (
    <div className="adm-media-upload-bar">
      <p className="adm-media-upload-hint">
        Pick what this photo is for, then choose the file. If a photo already exists for that slot you'll be asked to confirm replacement.
      </p>
      <div className="adm-media-upload-row">
        <select
          className="adm-media-slot-select"
          value={slot}
          onChange={e => { setSlot(e.target.value); setCustom(""); }}
        >
          {slots.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        {slot === "other" && (
          <input
            type="text"
            className="adm-media-label-input"
            placeholder="Describe this photo…"
            value={customLabel}
            onChange={e => setCustom(e.target.value)}
          />
        )}

        <input ref={fileRef} type="file" accept="image/*,image/svg+xml"
          style={{ display: "none" }} onChange={handleFileChange} />
        <button className="adm-publish-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? `Uploading… ${pct}%` : "Choose file →"}
        </button>
        <button className="adm-draft-btn" onClick={onCancel} disabled={uploading}>Cancel</button>
      </div>

      {uploading && (
        <div className="adm-media-progress">
          <div className="adm-media-progress-bar" style={{ width: `${pct}%` }} />
        </div>
      )}
      {existing && !uploading && (
        <div className="adm-media-slot-warn">
          ⚠ A photo already exists for this slot — you'll be asked to confirm replacement.
        </div>
      )}
      {err && <p style={{ color: "#e53e3e", fontSize: "0.8rem", marginTop: "0.4rem" }}>{err}</p>}
    </div>
  );
};

/* ─────────────────────────────────────────────────
   MAIN PANEL
───────────────────────────────────────────────── */
const MediaPanel = () => {
  const [media, setMedia]         = useState([]);
  const [phases, setPhases]       = useState([]);  // journey phases from Firestore
  const [loading, setLoading]     = useState(true);
  const [seeding, setSeeding]     = useState(false);
  const [seedMsg, setSeedMsg]     = useState("");
  const [showForm, setShowForm]   = useState(false);
  const [copied, setCopied]       = useState(null);
  const [filter, setFilter]       = useState("");

  /* Derived: full slot list that grows with journey phases */
  const allSlots = buildSlots(phases);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mediaSnap, phaseSnap] = await Promise.all([
        getDocs(query(collection(db, "media_library"), orderBy("uploadedAt", "desc"))),
        getDocs(query(collection(db, "journey_phases"), orderBy("order", "asc"))),
      ]);
      setMedia(mediaSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setPhases(phaseSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      setMedia([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Seed existing local images ── */
  const handleSeed = async () => {
    /* Build seed list dynamically: one entry per journey phase + fixed slots */
    const seedAssets = [
      /* Journey phases: map by order to the local images we have */
      { order: 1, src: imgKnowMe,      filename: "KnowMoreAboutMe.jpeg" },
      { order: 2, src: imgSkills,      filename: "Skills.jpeg"          },
      { order: 3, src: imgJourney,     filename: "Journey.jpeg"         },
      { order: 4, src: imgLetsConnect, filename: "LetsConnect.jpeg"     },
    ]
    .filter(a => {
      /* Only seed phases that actually exist, or fallback to 4 defaults */
      if (phases.length === 0) return true;
      return phases.some(p => p.order === a.order);
    })
    .map(a => ({
      slot: `journey-${a.order}`,
      src:  a.src,
      filename: a.filename,
    }))
    .concat([
      { slot: "contact-banner", src: imgContactMe, filename: "ContactMe.jpeg" },
      { slot: "hero-bg",        src: imgHeroJpg,   filename: "hero.jpg"       },
    ]);

    if (!window.confirm(
      `Upload ${seedAssets.length} existing site photos to the media library?\n\nFor each slot that already has a photo, you'll be asked whether to replace it.`
    )) return;

    setSeeding(true);
    setSeedMsg("");
    try {
      /* Fetch existing docs fresh */
      const snap = await getDocs(collection(db, "media_library"));
      const bySlot = {};
      snap.docs.forEach(d => { const data = d.data(); if (data.slot) bySlot[data.slot] = { id: d.id, ...data }; });

      for (let i = 0; i < seedAssets.length; i++) {
        const asset = seedAssets[i];
        const slotName = resolveLabel(asset.slot, allSlots, "");
        const existing = bySlot[asset.slot];

        if (existing) {
          const replace = window.confirm(
            `"${slotName}" already has a photo (${existing.filename || "unknown"}).\n\nReplace it with ${asset.filename}?`
          );
          if (!replace) { setSeedMsg(`Skipped "${slotName}"`); continue; }
        }

        setSeedMsg(`${i + 1}/${seedAssets.length} — uploading ${asset.filename}…`);
        const response = await fetch(asset.src);
        const blob = await response.blob();
        const storagePath = `library/${asset.slot}/${asset.filename}`;

        const { url, path } = await new Promise((resolve, reject) => {
          const storageRef = ref(storage, storagePath);
          const task = uploadBytesResumable(storageRef, blob);
          task.on("state_changed", null, reject, async () =>
            resolve({ url: await getDownloadURL(task.snapshot.ref), path: task.snapshot.ref.fullPath })
          );
        });

        const payload = {
          url, path, slot: asset.slot, customLabel: "",
          filename: asset.filename, size: blob.size, type: blob.type,
          uploadedAt: serverTimestamp(),
        };

        if (existing) {
          if (existing.path) { try { await deleteObject(ref(storage, existing.path)); } catch (_) {} }
          await setDoc(doc(db, "media_library", existing.id), payload);
        } else {
          await addDoc(collection(db, "media_library"), payload);
        }
      }

      setSeedMsg(`✓ Done!`);
      await load();
    } catch (e) {
      setSeedMsg("Error: " + e.message);
    } finally {
      setSeeding(false);
      setTimeout(() => setSeedMsg(""), 5000);
    }
  };

  /* ── Delete ── */
  const handleDelete = async (item) => {
    const name = resolveLabel(item.slot, allSlots, item.customLabel);
    if (!window.confirm(`Delete "${name}"?\nThis removes it from Firebase Storage and the library.`)) return;
    try {
      if (item.path) { try { await deleteObject(ref(storage, item.path)); } catch (_) {} }
      await deleteDoc(doc(db, "media_library", item.id));
      setMedia(prev => prev.filter(m => m.id !== item.id));
    } catch { alert("Delete failed."); }
  };

  /* ── Copy URL ── */
  const copyUrl = (item) => {
    navigator.clipboard.writeText(item.url).then(() => {
      setCopied(item.id);
      setTimeout(() => setCopied(null), 2200);
    });
  };

  /* Slots that still have no photo */
  const usedSlots = new Set(media.map(m => m.slot));
  const missingSlots = allSlots.filter(s => s.value !== "other" && !usedSlots.has(s.value));

  const filtered = filter.trim()
    ? media.filter(m => {
        const label = resolveLabel(m.slot, allSlots, m.customLabel).toLowerCase();
        return label.includes(filter.toLowerCase()) ||
               (m.filename || "").toLowerCase().includes(filter.toLowerCase());
      })
    : media;

  return (
    <section className="adm-panel adm-media-panel">
      <div className="adm-panel-head">
        <div>
          <h2>Media Library</h2>
          <p>
            All site photos in one place — slots grow automatically as you add journey chapters
          </p>
        </div>
        <div className="adm-media-head-actions">
          <span className="adm-panel-count">{media.length} photos</span>
          <button className="adm-new-blog" onClick={() => setShowForm(v => !v)}>
            {showForm ? "✕ Cancel" : "+ Upload Photo"}
          </button>
        </div>
      </div>

      {showForm && (
        <UploadForm
          media={media}
          slots={allSlots}
          onUploaded={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="adm-dash-loading" style={{ padding: "1.5rem" }}>
          <div className="adm-spinner" />
        </div>
      ) : media.length === 0 ? (
        <div className="adm-empty">
          <span>—</span>
          <p>No photos yet. Upload one above, or seed all existing site images below.</p>
          <button
            className="adm-publish-btn"
            onClick={handleSeed}
            disabled={seeding}
            style={{ marginTop: "0.75rem", fontSize: "0.88rem" }}
          >
            {seeding ? (seedMsg || "Uploading…") : `Seed existing site photos`}
          </button>
          {seedMsg && <p className="adm-media-seed-status" style={{ marginTop: "0.5rem" }}>{seedMsg}</p>}
        </div>
      ) : (
        <>
          <div className="adm-media-filter-bar">
            <input
              type="search"
              className="adm-media-filter"
              placeholder="Search by slot or filename…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
            <button
              className="adm-draft-btn"
              onClick={handleSeed}
              disabled={seeding}
              style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              title="Re-seed / add missing original photos"
            >
              {seeding ? "Uploading…" : "Seed originals"}
            </button>
          </div>

          {seedMsg && (
            <p className="adm-media-seed-status" style={{ padding: "0 1.5rem 0.75rem" }}>{seedMsg}</p>
          )}

          {/* Missing slots */}
          {missingSlots.length > 0 && (
            <div className="adm-media-missing">
              <strong>Missing:</strong>
              {missingSlots.map(s => (
                <button
                  key={s.value}
                  className="adm-media-missing-slot"
                  onClick={() => setShowForm(true)}
                  title={`Upload a photo for "${s.label}"`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="adm-empty" style={{ padding: "1.5rem" }}>
              <p>No photos match "{filter}".</p>
            </div>
          ) : (
            <div className="adm-media-grid">
              {filtered.map(item => {
                const name = resolveLabel(item.slot, allSlots, item.customLabel);
                return (
                  <div key={item.id} className="adm-media-card">
                    <div className="adm-media-thumb" style={{ backgroundImage: `url(${item.url})` }} />

                    <div className="adm-media-info">
                      <span className="adm-media-slot-badge">{name}</span>
                      {item.slot === "other" && item.customLabel && (
                        <span className="adm-media-custom-label">{item.customLabel}</span>
                      )}
                      <div className="adm-media-meta">
                        {item.filename && <span title={item.filename}>{item.filename}</span>}
                        {item.size > 0 && <span>{fmtSize(item.size)}</span>}
                      </div>
                    </div>

                    <div className="adm-media-actions">
                      <button
                        className={`adm-media-copy${copied === item.id ? " copied" : ""}`}
                        onClick={() => copyUrl(item)}
                        title="Copy Firebase Storage URL"
                      >
                        {copied === item.id ? "Copied!" : "Copy URL"}
                      </button>
                      <button
                        className="adm-btn-edit"
                        style={{ fontSize: "0.72rem", padding: "0.28rem 0.55rem" }}
                        onClick={() => setShowForm(true)}
                        title="Replace this photo"
                      >
                        ↺
                      </button>
                      <button
                        className="adm-btn-del"
                        onClick={() => handleDelete(item)}
                        title="Delete from Storage & library"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default MediaPanel;
